import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { hull } from "../utils/hull";
import "../styles/common.css";
import { processIndices, updatePlane } from "../utils/intersectionHelpers";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { escapeLoomTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { normalizeModel } from "../utils/normalizeModel";
import { normalize } from "three/src/math/MathUtils";
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';

// NEW: 3D 打印机预设
const MACHINE_PRESETS = {
  printer3D: {
    "Prusa MK3S+": { width: 250, height: 250, depth: 210 },
    "Ender 3 V2": { width: 220, height: 220, depth: 250 },
    "Ultimaker S3": { width: 230, height: 190, depth: 200 },
    Custom: { width: 10, height: 10, depth: 10 },
  },
};

const EscapeLoom = ({ stlFile }) => {
  const containerRef = useRef(null);

  // Three.js objects
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const objRef = useRef(null);
  const processingTimeoutRef = useRef(null);

  // Wire reference objects
  const verticalLinesRef = useRef(new THREE.Group());
  const horizontalLinesRef = useRef(new THREE.Group());
  const verticalMeshRef = useRef(new THREE.Group());
  const horizontalMeshRef = useRef(new THREE.Group());
  const verticalLabelGroupRef = useRef(new THREE.Group());
  const horizontalLabelGroupRef = useRef(new THREE.Group());

  // State with refs for frequently accessed values
  const [wireNumber, setWireNumber] = useState(10);
  const wireNumberRef = useRef(wireNumber);
  useEffect(() => {
    wireNumberRef.current = wireNumber;
  }, [wireNumber]);
  const [constraintWarnings, setConstraintWarnings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csvReady, setCsvReady] = useState(false);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [tutorialVisible, setTutorialVisible] = useState(false);

  const [concavity] = useState(20);
  const concavityRef = useRef(concavity);
  useEffect(() => {
    concavityRef.current = concavity;
  }, [concavity]);

  const [pointSimplificationThreshold] = useState(5);
  const pointSimplificationThresholdRef = useRef(pointSimplificationThreshold);
  useEffect(() => {
    pointSimplificationThresholdRef.current = pointSimplificationThreshold;
  }, [pointSimplificationThreshold]);

  const [expanded, setExpanded] = useState(false);
  // Wire forming + assembly is very manual & slow
  const fastIterNotRecommended = useMemo(() => true, []);

  const { createVisualization, removeVisualHelpers, createTextLabel } =
    useVisualHelpers();
  const pageId = useMemo(() => `wire-mesh`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  // Memoize materials for reuse
  const materials = useMemo(
    () => ({
      modelMaterial: new THREE.MeshNormalMaterial({
        transparent: true,
        opacity: 0.2,
        wireframe: false,
      }),
      planeMaterial: new THREE.MeshBasicMaterial({
        color: "lightgray",
        transparent: true,
        opacity: 0.75,
        side: THREE.DoubleSide,
      }),
      tubeMaterial: new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.3,
        metalness: 0.0,
      }),
      lineMaterial: new THREE.LineBasicMaterial({
        color: 0xff0000,
        visible: false,
      }),
    }),
    []
  );

  const geometrySettings = useMemo(
    () => ({
      planeGeometry: new THREE.PlaneGeometry(200, 200),
      tubeRadialSegments: 8,
      tubeRadius: 2, // Updated from 0.5 to 2 for thicker, more visible tubes
    }),
    []
  );

  // Initialize Three.js scene using the helper function
  const initScene = useCallback(() => {
    // Clear any existing elements that might be left over
    if (containerRef.current) {
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
    }

    // Use the helper function to initialize the scene
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    // Add wire groups to scene
    if (sceneRef.current) {
      // Create custom grid helper with specific settings
      const gridHelper = new THREE.GridHelper(200, 20, 0x444444, 0x888888);
      gridHelper.position.y = -0.01;
      sceneRef.current.add(gridHelper);

      // Create custom axis helper with specific length
      const axesHelper = new THREE.AxesHelper(50);
      sceneRef.current.add(axesHelper);

      // Add wire groups
      sceneRef.current.add(verticalLinesRef.current);
      sceneRef.current.add(horizontalLinesRef.current);
      sceneRef.current.add(verticalMeshRef.current);
      sceneRef.current.add(horizontalMeshRef.current);
      sceneRef.current.add(verticalLabelGroupRef.current);
      sceneRef.current.add(horizontalLabelGroupRef.current);
    }

    // Handle window resize (only add once)
    const handleResize = () => {
      if (cameraRef.current && rendererRef.current && containerRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        cameraRef.current.aspect = width / height;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(width, height);
      }
    };
    window.addEventListener("resize", handleResize);

    // Return combined cleanup function
    return () => {
      window.removeEventListener("resize", handleResize);
      if (cleanup) cleanup();
    };
  }, []);

  // Throttle operations to prevent excessive updates
  const throttle = useCallback((fn, delay = 0) => {
    let lastCall = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return fn(...args);
    };
  }, []);

  // Optimized point simplification with early returns and caching
  const simplifyPointsByDistance = useCallback(
    (points, distanceThreshold = 5, angleThreshold = Math.PI / 12) => {
      if (!points || points.length <= 1) return points;

      const angleSimplifiedPoints = [];
      let lastPoint = points[0];
      angleSimplifiedPoints.push(lastPoint);

      // First simplify by angle - more efficient loop
      for (let i = 1; i < points.length - 1; i++) {
        const prevPoint = lastPoint;
        const currentPoint = points[i];
        const nextPoint = points[i + 1];

        // Calculate angle between vectors - reuse vector objects
        const vector1 = new THREE.Vector3()
          .subVectors(currentPoint, prevPoint)
          .normalize();
        const vector2 = new THREE.Vector3()
          .subVectors(nextPoint, currentPoint)
          .normalize();
        const dot = vector1.dot(vector2);
        // Use faster math operations
        const angle = Math.acos(Math.max(-1, Math.min(1, dot)));

        // Keep point if angle is significant
        if (angle > angleThreshold) {
          angleSimplifiedPoints.push(currentPoint);
          lastPoint = currentPoint;
        }
      }

      // Always include last point
      if (points.length > 1) {
        angleSimplifiedPoints.push(points[points.length - 1]);
      }

      // Skip second pass if we've already simplified enough
      if (angleSimplifiedPoints.length <= 2) return angleSimplifiedPoints;

      // Then simplify by distance
      const distanceSimplifiedPoints = [];
      lastPoint = angleSimplifiedPoints[0];
      distanceSimplifiedPoints.push(lastPoint);

      for (let i = 1; i < angleSimplifiedPoints.length; i++) {
        if (
          lastPoint.distanceTo(angleSimplifiedPoints[i]) > distanceThreshold
        ) {
          distanceSimplifiedPoints.push(angleSimplifiedPoints[i]);
          lastPoint = angleSimplifiedPoints[i];
        }
      }

      // Always ensure the last point is included
      const lastOriginalPoint =
        angleSimplifiedPoints[angleSimplifiedPoints.length - 1];
      const lastSimplifiedPoint =
        distanceSimplifiedPoints[distanceSimplifiedPoints.length - 1];

      if (lastSimplifiedPoint !== lastOriginalPoint) {
        distanceSimplifiedPoints.push(lastOriginalPoint);
      }

      return distanceSimplifiedPoints;
    },
    []
  );

  // Optimized total length calculation with error handling
  const calculateTotalLength = useCallback((points) => {
    if (!points || points.length <= 1) return 0;

    let totalLength = 0;
    for (let i = 1; i < points.length; i++) {
      totalLength += points[i - 1].distanceTo(points[i]);
    }
    return totalLength;
  }, []);

  // Enhanced clear wires function with better cleanup
  const clearWires = useCallback(() => {
    // Cancel any in-progress processing
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    // Helper function to remove all children
    const clearGroup = (group) => {
      while (group.children.length > 0) {
        const child = group.children[0];
        group.remove(child);
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    };

    // Clear all wire groups
    clearGroup(verticalLinesRef.current);
    clearGroup(horizontalLinesRef.current);
    clearGroup(verticalMeshRef.current);
    clearGroup(horizontalMeshRef.current);
    clearGroup(verticalLabelGroupRef.current);
    clearGroup(horizontalLabelGroupRef.current);
  }, []);

  // Optimized shape creation
  const createShapeFromHull = useCallback((hullPoints) => {
    if (!hullPoints || hullPoints.length <= 2) return null;

    const shape = new THREE.Shape();

    hullPoints.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point[0], point[1]);
      } else {
        shape.lineTo(point[0], point[1]);
      }
    });

    shape.closePath();
    return shape;
  }, []);

  // Create tube for a wire
  const createWireTube = useCallback(
    (simplifiedPoints, tubularSegments = 30) => {
      if (!simplifiedPoints || simplifiedPoints.length < 2) return null;

      const curvePath = new THREE.CurvePath();

      for (let i = 0; i < simplifiedPoints.length - 1; i++) {
        const lineCurve = new THREE.LineCurve3(
          simplifiedPoints[i],
          simplifiedPoints[i + 1]
        );
        curvePath.add(lineCurve);
      }

      const tubeGeometry = new THREE.TubeGeometry(
        curvePath,
        tubularSegments,
        geometrySettings.tubeRadius,
        geometrySettings.tubeRadialSegments,
        false // not closed
      );

      return tubeGeometry;
    },
    [geometrySettings]
  );

  // Process horizontal wire
  const processHorizontalWire = useCallback(
    (obj, i, planeHeight, size, maxWireLength) => {
      const intersectionPoints = [];
      const vertices = obj.geometry.attributes.position;
      const indices = obj.geometry.index;

      // Create horizontal plane
      const planeGeometry = geometrySettings.planeGeometry.clone();
      planeGeometry.rotateX(-Math.PI / 2);

      const plane = new THREE.Mesh(planeGeometry, materials.planeMaterial);
      plane.position.y = size.y / wireNumberRef.current / 2 + i * planeHeight;
      plane.updateMatrixWorld(true);

      // Calculate intersection points
      const mathPlane = new THREE.Plane();
      updatePlane(mathPlane, plane);
      processIndices(indices, vertices, obj, mathPlane, intersectionPoints);

      if (intersectionPoints.length <= 3) return;

      // Calculate average Y position more efficiently
      let averageY = 0;
      for (let j = 0; j < intersectionPoints.length; j++) {
        averageY += intersectionPoints[j].y;
      }
      averageY /= intersectionPoints.length;

      // Generate horizontal wire contour
      const points2D = intersectionPoints.map((p) => [p.x, p.z]);
      const hullPoints = hull(points2D, concavityRef.current);

      if (!hullPoints || hullPoints.length <= 2) return;

      // Create shape
      const shape = createShapeFromHull(hullPoints);
      if (!shape) return;

      // Create 3D points array
      const points = [];
      hullPoints.forEach((point) => {
        points.push(new THREE.Vector3(point[0], averageY, point[1]));
      });

      // Close the loop by adding the first point again
      points.push(points[0].clone());

      // Simplify points
      const simplifiedPoints = simplifyPointsByDistance(
        points,
        pointSimplificationThresholdRef.current
      );

      // Create tube geometry for the wire
      if (simplifiedPoints.length <= 1) return;

      const tubeGeometry = createWireTube(simplifiedPoints, 30);
      if (!tubeGeometry) return;

      const tube = new THREE.Mesh(tubeGeometry, materials.tubeMaterial);
      tube.castShadow = true;
      horizontalLinesRef.current.add(tube);

      // Check wire length for fabrication limitations
      const wireLength = calculateTotalLength(simplifiedPoints);
      if (wireLength > maxWireLength) {
        //console.warn(`Horizontal wire ${i + 1} length (${wireLength.toFixed(2)}mm) exceeds recommended maximum (${maxWireLength}mm)`);
      }

      // Add label
      const labelPosition = new THREE.Vector3(0, averageY, size.z / 2);
      const label = createTextLabel(`Horizontal ${i + 1}`, labelPosition);
      horizontalLabelGroupRef.current.add(label);
    },
    [
      geometrySettings,
      materials,
      createShapeFromHull,
      simplifyPointsByDistance,
      calculateTotalLength,
      createWireTube,
      createTextLabel,
    ]
  );

  // Process vertical wire
  const processVerticalWire = useCallback(
    (obj, i, planeStep, boundingBox, size, maxWireLength) => {
      const intersectionPoints = [];
      const vertices = obj.geometry.attributes.position;
      const indices = obj.geometry.index;

      // Create vertical plane
      const planeGeometry = geometrySettings.planeGeometry.clone();

      const plane = new THREE.Mesh(planeGeometry, materials.planeMaterial);
      plane.position.z =
        boundingBox.min.z + size.z / wireNumberRef.current / 2 + i * planeStep;
      plane.position.y = size.y / 2;
      plane.updateMatrixWorld(true);

      // Calculate intersection points
      const mathPlane = new THREE.Plane();
      updatePlane(mathPlane, plane);
      processIndices(indices, vertices, obj, mathPlane, intersectionPoints);

      if (intersectionPoints.length <= 3) return;

      // Calculate average Z position more efficiently
      let averageZ = 0;
      for (let j = 0; j < intersectionPoints.length; j++) {
        averageZ += intersectionPoints[j].z;
      }
      averageZ /= intersectionPoints.length;

      // Generate vertical wire contour
      const points2D = intersectionPoints.map((p) => [p.x, p.y]);
      const hullPoints = hull(points2D, concavityRef.current);

      if (!hullPoints || hullPoints.length <= 2) return;

      // Create shape
      const shape = createShapeFromHull(hullPoints);
      if (!shape) return;

      // Create 3D points array
      const vertPoints = [];
      hullPoints.forEach((point) => {
        vertPoints.push(new THREE.Vector3(point[0], point[1], averageZ));
      });

      // Close the loop by adding the first point again
      vertPoints.push(vertPoints[0].clone());

      // Simplify points
      const simplifiedPoints = simplifyPointsByDistance(
        vertPoints,
        pointSimplificationThresholdRef.current
      );

      // Create tube geometry for the wire
      if (simplifiedPoints.length <= 1) return;

      const vertTubeGeometry = createWireTube(simplifiedPoints, 100);
      if (!vertTubeGeometry) return;

      const vertTube = new THREE.Mesh(vertTubeGeometry, materials.tubeMaterial);
      vertTube.castShadow = true;
      verticalLinesRef.current.add(vertTube);

      // Check wire length for fabrication limitations
      const wireLength = calculateTotalLength(simplifiedPoints);
      if (wireLength > maxWireLength) {
        //console.warn(`Vertical wire ${i + 1} length (${wireLength.toFixed(2)}mm) exceeds recommended maximum (${maxWireLength}mm)`);
      }

      // Add label
      const labelPosition = new THREE.Vector3(0, size.y + 10, averageZ);
      const label = createTextLabel(
        `Vertical ${i + 1}`,
        labelPosition,
        Math.PI / 4
      );
      verticalLabelGroupRef.current.add(label);
    },
    [
      geometrySettings,
      materials,
      createShapeFromHull,
      simplifyPointsByDistance,
      calculateTotalLength,
      createWireTube,
      createTextLabel,
    ]
  );

  // Main wire mesh generation function - optimized with fewer timeouts and better memory usage
  const generateWireMesh = useCallback(() => {
    if (!objRef.current || !sceneRef.current) return;

    // Clear previous wires
    clearWires();

    const obj = objRef.current;

    // Get bounding box and dimensions once
    const boundingBox = new THREE.Box3().setFromObject(obj);
    const size = boundingBox.getSize(new THREE.Vector3());

    // Maximum allowed values for fabrication limitations
    const maxWireLength = 1000; // 1 meter
    const maxWireCount = 10;
    const maxBoundingBoxSize = {
      width: 2000, // 2 meters
      height: 2000, // 2 meters
      depth: 500, // 0.5 meters
    };

    // Check if model is within fabrication limits
    if (
      size.x > maxBoundingBoxSize.width ||
      size.y > maxBoundingBoxSize.height ||
      size.z > maxBoundingBoxSize.depth
    ) {
      //console.warn("Model exceeds recommended size limits for fabrication");
    }

    if (wireNumberRef.current > maxWireCount) {
      //console.warn(`Wire count of ${wireNumberRef.current} exceeds the recommended limit of ${maxWireCount}`);
    }

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      // Process all wires in a batch for better performance
      const wireCount = wireNumberRef.current;
      const planeHeight = size.y / wireCount;
      const planeStep = size.z / wireCount;

      // Process horizontal wires
      for (let i = 0; i < wireCount; i++) {
        processHorizontalWire(obj, i, planeHeight, size, maxWireLength);
      }

      // Process vertical wires
      for (let i = 0; i < wireCount; i++) {
        processVerticalWire(
          obj,
          i,
          planeStep,
          boundingBox,
          size,
          maxWireLength
        );
      }

      // Update state after processing
      setIsLoading(false);
      setCsvReady(true);
    });
  }, [clearWires, processHorizontalWire, processVerticalWire]);

  // Optimized model loading function
  const loadModel = useCallback(
    (filePath) => {
      setIsLoading(true);
      setError(null);
      setCsvReady(false);
      //console.log("Loading model: ", filePath);

      // Clear any pending timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }

      // Clear any existing model
      if (objRef.current) {
        sceneRef.current?.remove(objRef.current);
        objRef.current.geometry?.dispose();
        if (Array.isArray(objRef.current.material)) {
          objRef.current.material.forEach((m) => m.dispose());
        } else {
          objRef.current.material?.dispose();
        }
        objRef.current = null;
      }

      const loader = new STLLoader();
      const path = filePath || "/models/default.stl";

      loader.load(
        path,
        (geometry) => {
          // Log successful loading
          //console.log("Model loaded successfully");

          try {
            // Compute the bounding box BEFORE transformations
            geometry.computeBoundingBox();
            const boundingBox = geometry.boundingBox.clone(); // Store original bounding box

            // Apply transformations
            geometry.translate(
              -boundingBox.getCenter(new THREE.Vector3()).x,
              -boundingBox.getCenter(new THREE.Vector3()).y,
              -boundingBox.getCenter(new THREE.Vector3()).z
            );

            // Create mesh - reuse material
            const mesh = new THREE.Mesh(geometry, materials.modelMaterial);
            normalizeModel(mesh);

            // 计算变换后的包围盒与尺寸
            const transformedBoundingBox = new THREE.Box3().setFromObject(mesh);
            const size = new THREE.Vector3();
            transformedBoundingBox.getSize(size);
            setModelDimensions({
              width: size.x,
              height: size.y,
              depth: size.z,
            });

            // ★★★ 预检：模型尺寸 vs 打印机工作腔
            const selectedPrinter =
              window?.labConstraints?.machineSettings?.printer3D?.selected ||
              "Prusa MK3S+";
            const limits =
              MACHINE_PRESETS.printer3D[selectedPrinter] ||
              MACHINE_PRESETS.printer3D["Prusa MK3S+"];

            const exceeds =
              size.x > limits.width ||
              size.y > limits.height ||
              size.z > limits.depth;

            if (exceeds) {
              setConstraintWarnings([
                {
                  type: "size_exceeded",
                  part: "Escape Loom Model",
                  currentSize: {
                    width: size.x.toFixed(1),
                    height: size.y.toFixed(1),
                    depth: size.z.toFixed(1),
                  },
                  maxSize: limits,
                  printer: selectedPrinter,
                },
              ]);

              // 不做任何重计算 / 不生成 wire / 不可视化
              setIsLoading(false);
              return; // ★★★ 直接停止
            } else {
              setConstraintWarnings([]); // 清空旧警告
            }

            // 通过预检才继续
            objRef.current = mesh;
            // 可选择不把 mesh 真加到 scene（你原来就是用 helper 可视化）
            removeVisualHelpers(sceneRef.current);
            createVisualization(
              objRef.current,
              sceneRef.current,
              transformedBoundingBox
            );

            // 生成 wire
            requestAnimationFrame(generateWireMesh);
          } catch (error) {
            //console.error("Error processing the loaded model:", error);
            setError("Error processing the 3D model");
            setIsLoading(false);
          }
        },
        // Progress callback - update loader status
        (progress) => {
          const percent = Math.round((progress.loaded / progress.total) * 100);
          //console.log(`Loading progress: ${percent}%`);
        },
        // Error callback
        (error) => {
          //console.error('Error loading STL:', error);
          setError("Failed to load 3D model");
          setIsLoading(false);
        }
      );
    },
    [
      generateWireMesh,
      createVisualization,
      removeVisualHelpers,
      materials.modelMaterial,
    ]
  );

/*   // Export a group of wire lines to CSV - optimized for efficiency
  const exportLineGroup = useCallback(
    (lineGroup, baseFilename) => {
      //console.log(`Exporting lines for: ${baseFilename}`);

      // Use array to batch file creations
      const fileDownloads = [];

      for (let index = 0; index < lineGroup.children.length; index++) {
        const line = lineGroup.children[index];

        // Skip if no geometry or it's not a tube
        if (
          !line.geometry ||
          !line.geometry.parameters ||
          !line.geometry.parameters.path
        ) {
          continue;
        }

        // Get points from the tube's path
        const path = line.geometry.parameters.path;
        const points = path.curves.flatMap((curve) => [curve.v1, curve.v2]);

        // Remove duplicates more efficiently
        const uniquePoints = [];
        let lastPoint = null;

        for (const point of points) {
          if (!lastPoint || !point.equals(lastPoint)) {
            uniquePoints.push(point);
            lastPoint = point;
          }
        }

        // Simplify points
        const simplifiedPoints = simplifyPointsByDistance(
          uniquePoints,
          pointSimplificationThresholdRef.current
        );

        // Generate CSV content with string concatenation optimization
        const rows = ["x,y,z"];
        simplifiedPoints.forEach((point) => {
          rows.push(
            `${point.x.toFixed(2)},${point.y.toFixed(2)},${point.z.toFixed(2)}`
          );
        });
        const csvContent = rows.join("\n");

        // Create file info for batch processing
        fileDownloads.push({
          content: csvContent,
          filename: `${baseFilename}_${index + 1}.csv`,
        });
      }

      // Batch download files with a small delay between them
      fileDownloads.forEach((file, i) => {
        setTimeout(() => {
          const blob = new Blob([file.content], { type: "text/csv" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.filename;
          document.body.appendChild(a);
          a.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }, 100);
        }, i * 200); // Small delay between downloads
      });
    },
    [simplifyPointsByDistance]
  ); */

  const exportSTL = useCallback(() => {
  if (!csvReady) {
    alert("请先生成线条网格");
    return;
  }

  // 检查是否有约束警告
  if (constraintWarnings.length > 0) {
    alert("模型超出打印机尺寸限制，无法导出");
    return;
  }

  // 创建一个组合所有线条的群组
  const combinedGroup = new THREE.Group();
  
  // 添加所有水平线条
  horizontalLinesRef.current.children.forEach(mesh => {
    if (mesh.geometry) {
      const clonedMesh = mesh.clone();
      combinedGroup.add(clonedMesh);
    }
  });
  
  // 添加所有垂直线条
  verticalLinesRef.current.children.forEach(mesh => {
    if (mesh.geometry) {
      const clonedMesh = mesh.clone();
      combinedGroup.add(clonedMesh);
    }
  });

  if (combinedGroup.children.length === 0) {
    alert("没有可导出的线条");
    return;
  }

  // 导出为 STL
  const exporter = new STLExporter();
  const result = exporter.parse(combinedGroup);
  const blob = new Blob([result], { type: "text/plain" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "escape_loom_wire_mesh.stl";
  document.body.appendChild(link);
  link.click();

  // 清理
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);

  // 显示教程
  setTimeout(() => {
    setTutorialVisible(true);
  }, 500);
}, [csvReady, constraintWarnings]);

/*   // Export wire paths as CSV files - more efficient batch approach
  const exportCSV = useCallback(() => {
    // Export wires with minimal delay
    exportLineGroup(horizontalLinesRef.current, "horizontal_wire");

    setTimeout(() => {
      exportLineGroup(verticalLinesRef.current, "vertical_wire");

      // Show tutorial after export is complete
      setTimeout(() => {
        setTutorialVisible(true);
      }, 500);
    }, 500);
  }, [exportLineGroup]); */

  // Debounced processing for parameter changes
  const debouncedGenerateWireMesh = useCallback(
    throttle(() => {
      if (objRef.current && sceneRef.current) {
        // Clear any existing timeout
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }

        // Schedule new processing
        processingTimeoutRef.current = setTimeout(() => {
          generateWireMesh();
          processingTimeoutRef.current = null;
        }, 0);
      }
    }, 250), // Shorter delay for better responsiveness
    [generateWireMesh]
  );

  // Initialize scene and load model
  useEffect(() => {
    const cleanup = initScene();

    // Store ref value in a variable to use in cleanup
    const currentSceneRef = sceneRef.current;

    // Load default model if no file provided
    const defaultModelPath = stlFile || "/models/default.stl";
    loadModel(defaultModelPath);

    return () => {
      // Clear any pending timeout
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }

      // Clean up visual helpers using the stored ref
      if (currentSceneRef) {
        removeVisualHelpers(currentSceneRef);
      }

      // Clear all wires
      clearWires();

      // Call the cleanup function
      if (cleanup) cleanup();

      // Additional cleanup for this specific component
      if (currentSceneRef) {
        currentSceneRef.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((m) => m.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
      }
    };
  }, [initScene, loadModel, stlFile, removeVisualHelpers, clearWires]);

  // Update when parameters change - use the debounced function
  useEffect(() => {
    debouncedGenerateWireMesh();
  }, [
    wireNumber,
    concavity,
    pointSimplificationThreshold,
    debouncedGenerateWireMesh,
  ]);

  // Update when stlFile changes
  useEffect(() => {
    if (stlFile && sceneRef.current) {
      //console.log("STL file changed to:", stlFile);
      loadModel(stlFile);
    }
  }, [stlFile, loadModel]);

  // Toggle tutorial visibility
  const toggleTutorial = useCallback(() => {
    setTutorialVisible((prev) => !prev);
  }, []);

  return (
    <div className="container">
      <button
        className="panel-toggle-button"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      <div ref={containerRef} className="canvas-container"></div>
      {constraintWarnings.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {constraintWarnings.map((w, i) => (
            <div
              key={i}
              style={{
                backgroundColor: "#ff9800",
                color: "white",
                padding: "12px 16px",
                borderRadius: "6px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                maxWidth: "90vw",
                width: "fit-content",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "6px",
                }}
              >
                <span style={{ fontSize: "16px", marginRight: "8px" }}>⚠️</span>
                <strong>
                  {w.part} Exceeds {w.printer} Size
                </strong>
              </div>
              <div style={{ fontSize: "13px" }}>
                <div>
                  Current: {w.currentSize.width} × {w.currentSize.height} ×{" "}
                  {w.currentSize.depth} mm
                </div>
                <div>
                  Printer Max: {w.maxSize.width} × {w.maxSize.height} ×{" "}
                  {w.maxSize.depth} mm
                </div>
              </div>
              <button
                onClick={() =>
                  setConstraintWarnings((prev) =>
                    prev.filter((_, idx) => idx !== i)
                  )
                }
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Floating Control Panel - Below dimensions box */}
      {!isLoading && expanded && (
        <div className="controls-panel">
          <div className="panel-title-row">
            <div className="panel-title">Escape Loom</div>
            {fastIterNotRecommended && (
              <span
                className="iteration-badge badge--warn"
                title="Wire bending + jig/fixture + assembly = long manual time"
              >
                Not ideal for quick iteration
              </span>
            )}
          </div>

          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
              <p>
                Based on the workflow from <em>Escape Loom</em>, this method
                uses a 3D printed support structure to assist in the creation of
                complex 3D weaving patterns. The process slices the model in
                horizontal and vertical planes to generate layered contours that
                serve as guides for weaving.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Ideal for building abstract, minimalistic, or intricate woven
                forms with reduced material waste.
              </p>
            </div>
          </div>

          {/* Collapsible Section Toggle - clickable */}
          <div
            className="section-toggle"
            onClick={() => setExpanded(!expanded)}
            style={{ cursor: "pointer" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                marginRight: "5px",
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <path d="M8 12L2 6H14L8 12Z" fill="#333" />
            </svg>
            3d_mesh_structure
          </div>

          {/* Panel content - conditionally rendered based on expanded state */}
          {expanded && (
            <div className="panel-content">
              <div className="input-group">
                <label>Mesh_number</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="1"
                    max="20"
                    value={wireNumber}
                    onChange={(e) =>
                      setWireNumber(parseInt(e.target.value) || 1)
                    }
                    disabled={isLoading}
                  />
                  <div className="value">{wireNumber}</div>
                </div>
              </div>
            </div>
          )}

          {/* Buttons outside the collapsible section - Always visible */}
          <div className="buttons-container">
            <button
              onClick={exportSTL}
              className="action-button"
              disabled={!csvReady || isLoading || constraintWarnings.length > 0} // NEW
            >
              EXPORT STL FILES
            </button>

            <button onClick={toggleTutorial} className="action-button">
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}

      {/* Loading Message */}
      {isLoading && <div className="loading-box">Loading model...</div>}

      {/* Error Message */}
      {error && <div className="error-box">Error: {error}</div>}

      {/* Using the TutorialWindow component */}
      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={toggleTutorial}
        tutorialContent={escapeLoomTutorial}
      />
    </div>
  );
};

export default EscapeLoom;
